import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { FullHeightBox } from '../boxes';
import useIsMobile from '../../hooks/useIsMobile';
import useTabsRouting from '../../hooks/useTabsRouting';
import SidebarItemsList from '../lists/SidebarItemsList';
import { SIDEBAR_DRAWER_CLASS_NAME } from '../../theme/constants/drawer';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';

const Sidebar = ({ options, showLogOut, goBack, useHideDrawer }) => {
  const isMobile = useIsMobile('lg');
  const [open, setOpen] = useState(!goBack);
  const { handleBack, renderRoutes } = useTabsRouting(options);

  useEffect(() => {
    if (goBack) {
      setOpen(false);
      return;
    }
    setOpen(!isMobile);
  }, [isMobile, goBack]);

  const toggleDrawer = () => {
    setOpen((prevState) => !prevState);
  };

  return (
    <FullHeightBox
      sx={(_theme) => ({
        display: 'flex',
        flexDirection: 'row',
        pr: { xs: 0, md: 4 },
        py: { xs: 0.5 },
        [_theme.breakpoints.down('md')]: {
          pl: 0,
          flexDirection: 'column',
        },
      })}
    >
      {goBack && (
      <Button
        startIcon={<ArrowBackOutlinedIcon className={DEFAULT_ICON_CLASS_NAME} />}
        onClick={handleBack}
        sx={{
          display: { xs: 'auto', md: 'none' },
          width: 'fit-content',
          py: 1,
        }}
        id="go-back-mobile"
      >
        Volver
      </Button>
      )}
      <MuiDrawer
        variant="permanent"
        open={open}
        className={SIDEBAR_DRAWER_CLASS_NAME}
        anchor="left"
        onClose={toggleDrawer}
      >
        {useHideDrawer && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: theme.spacing(0, 1),
          })}
        >
          {goBack ? (
            <IconButton onClick={handleBack} color="primary" id="go-back">
              <ArrowBackOutlinedIcon className={DEFAULT_ICON_CLASS_NAME} />
            </IconButton>
          ) : (
            <IconButton onClick={toggleDrawer} id="side-collapse">
              {open ? (
                <>
                  <KeyboardDoubleArrowLeft />
                  <Typography>Ocultar</Typography>
                </>
              ) : (
                <KeyboardDoubleArrowRight />
              )}
            </IconButton>
          )}
        </Box>
        )}
        <SidebarItemsList
          items={options}
          showLogOut={showLogOut}
          open={open}
        />
      </MuiDrawer>
      {renderRoutes()}
    </FullHeightBox>
  );
};

Sidebar.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      path: PropTypes.string,
      icon: PropTypes.element,
      component: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.func,
        PropTypes.array,
      ]),
      showAtBottom: PropTypes.bool,
    }),
  ).isRequired,
  showLogOut: PropTypes.bool,
  goBack: PropTypes.bool,
  useHideDrawer: PropTypes.bool,
};

Sidebar.defaultProps = {
  showLogOut: false,
  goBack: false,
  useHideDrawer: true,
};

export default Sidebar;
